import { LocalDateTime } from 'js-joda';

import { SQUARE_INCREMENT_MINUTES } from 'constants/TableBasedTemplatesConstants';
import { every, flatMap, forEach, map, orderBy, range, size } from 'es-toolkit/compat';

export interface TimeItem<T> {
  dateTime: LocalDateTime;
  kind: 'START' | 'END';
  item: T;
  type: 'GROUP' | 'TABLE' | 'NOTHING';
}

interface InventorySquareDescription {
  id: number | string;
  startDateTime: LocalDateTime;
  anticipatedEndDateTime: LocalDateTime;
  height?: number;
  tableId?: number | null;
  tableGroupId?: number | null;
}

export interface InternalSlotDetailsByDateTime<T extends InventorySquareDescription> {
  [dateTime: string]: {
    items: T[];
    startConflict?: boolean;
    endConflict?: boolean;
    numNotYetEnded: number;
  };
}

/**
 * Wrapper function for other methods that are generally called together
 *
 * @param items The list of all times to render
 * @param turnTimesBysize A mapping of party size and ticket type id to turn time
 * @returns A mapping of time to what needs to render at that dateTime
 */
export function computeSlotDetailsByDateTime<T extends InventorySquareDescription>(
  items: T[]
): { maxRow: number; renderDetailsByDateTime: InternalSlotDetailsByDateTime<T & { row: number }> } {
  return computeSlotDetailsByDateTimeFromTimeItems(gatherTimesToScan(items));
}

/**
 * Visible For Testing
 *
 * Takes a sorted list of start and end times for each piece of inventory (for one table),
 * and determines what needs to happen when rendering at each of those times.
 * The algorithm determines, for each time, whether there is a piece of inventory at that time
 * and whether the time is a start and/or end of a conflict block.
 *
 * The way the algorithm works is that it tracks the number of rows needed for the table and
 * which of them are opened. Every time it reaches a "start" time, it determines what row
 * it should be in by looking for the first open row or adding a new one if necessary.
 * It also tracks how many "starts" we've seen without a pairing close. If there are now two
 * open "starts", we know that a conflict just started.
 * Whenever it encounters an "end" block it decrements the number of open times and opens
 * up the row associated with the piece of inventory that it just reached the end of. If the number
 * "open" is exactly one, then it knows it just exited a conflict area.
 *
 * @param timesToScan A sorted list of "start" and "end" time of each piece of inventory
 * @returns A mapping of time to what needs to render at that dateTime
 */
export function computeSlotDetailsByDateTimeFromTimeItems<T extends InventorySquareDescription>(
  timeItemsToScan: Array<TimeItem<T>>
): { maxRow: number; renderDetailsByDateTime: InternalSlotDetailsByDateTime<T & { row: number }> } {
  const renderDetailsByDateTime: InternalSlotDetailsByDateTime<T & { row: number }> = {};
  const rowToItem: Array<T | undefined> = [];
  let numNotYetEnded = 0;
  forEach(timeItemsToScan, (timeItem) => {
    const dateTimeString = timeItem.dateTime.toString();
    const timeItemItem = timeItem.item;
    const itemHeight = timeItemItem.height || 1;
    if (timeItem.kind === 'START') {
      numNotYetEnded++;
      let resultingIndex;
      // Find the first open row, or add some if needed
      const openRowIndex = findNextAvailableRowWithSpace(rowToItem, itemHeight);
      if (openRowIndex < 0) {
        const itemsOfHeight = map(range(itemHeight), () => timeItemItem);
        itemsOfHeight.forEach((item) => rowToItem.push(item));
        resultingIndex = size(rowToItem) - itemHeight;
      } else {
        for (let i = 0; i < itemHeight; i++) {
          rowToItem[openRowIndex + i] = timeItemItem;
        }
        resultingIndex = openRowIndex;
      }

      // The reason we mutate the object inline here is because this needs to be high performant.
      const renderDetailsAtTime = renderDetailsByDateTime[dateTimeString] || { items: [] };
      renderDetailsAtTime.items.push({
        ...timeItemItem,
        row: resultingIndex,
      });
      renderDetailsByDateTime[dateTimeString] = renderDetailsAtTime;

      // If this is the second open time, a conflict has just started
      if (numNotYetEnded === 2) {
        renderDetailsByDateTime[dateTimeString].startConflict = true;
      }
    } else {
      numNotYetEnded--;
      // Clear the rows associated with this item
      for (let i = 0; i < rowToItem.length; i++) {
        const itemAtIndex = rowToItem[i];
        if (itemAtIndex && itemAtIndex.id === timeItemItem.id) {
          rowToItem[i] = undefined;
        }
      }
      renderDetailsByDateTime[dateTimeString] = renderDetailsByDateTime[dateTimeString] || {
        items: [],
      };
      // If there's now only one open time, the conflict has ended
      if (numNotYetEnded === 1) {
        renderDetailsByDateTime[dateTimeString].endConflict = true;
      }
    }
    renderDetailsByDateTime[dateTimeString].numNotYetEnded = numNotYetEnded;
  });
  // We never decrease the size of rowToItem, we just set entries to `undefined`
  // So however large the array is when we get to the end of the loop is the max height we need
  const maxRow = size(rowToItem);
  return {
    maxRow,
    renderDetailsByDateTime,
  };
}

/**
 * Returns the lowest index i into rowToItem such that all entries between i and (i + itemHeight) are undefined,
 * or -1 if no such index exists
 * @param rowToItem The current array of items / empty rows
 * @param itemHeight The height of the item in question
 * @returns The starting row index that can fit the item, or -1 if none exist
 */
function findNextAvailableRowWithSpace<T>(
  rowToItem: Array<T | undefined>,
  itemHeight: number
): number {
  // Fast path for the common case where we are placing an item with height 1. This dramatically
  // speeds up this calculation, which is in the inner loop of calendar rendering.
  if (itemHeight === 1) {
    for (let windowStart = 0; windowStart + itemHeight <= rowToItem.length; windowStart++) {
      if (rowToItem[windowStart] === undefined) {
        return windowStart;
      }
    }
    return -1;
  } else {
    for (let windowStart = 0; windowStart + itemHeight <= rowToItem.length; windowStart++) {
      const window = rowToItem.slice(windowStart, windowStart + itemHeight);
      if (every(window, (v): v is undefined => v === undefined)) {
        return windowStart;
      }
    }
    return -1;
  }
}

/**
 * Visible For Testing
 *
 * Builds the necessary data structure for the scanline algorithm. Specifically, goes
 * through the items, and figures out their start and end time, and then sorts
 * by the datetime so that they're in order for the next step of the algorithm;
 *
 * @param items The list of all times to render
 * @param turnTimesBysize A mapping of party size and ticket type id to turn time
 * @returns A sorted list of "start" and "end" time of each item info
 */
export function gatherTimesToScan<T extends InventorySquareDescription>(
  items: T[]
): Array<TimeItem<T>> {
  return orderBy(
    flatMap(items, (item) => {
      const startLocalDateTime = item.startDateTime;
      let endLocalDateTime = item.anticipatedEndDateTime;
      // If the end time is after the start time, we need to subtract 15 minutes to get the scan line
      // algorithm to work correctly because the last segment we care about is the one 15 minutes before the end time.
      // It's not valid for endLocalDateTime to *not* be after startLocalDateTime, but defensively only
      // subtract 15 minutes if it's after.
      if (endLocalDateTime.isAfter(startLocalDateTime)) {
        endLocalDateTime = endLocalDateTime.minusMinutes(SQUARE_INCREMENT_MINUTES);
      }

      let type;
      if (item.tableId) {
        type = 'TABLE';
      } else if (item.tableGroupId) {
        type = 'GROUP';
      } else {
        type = 'NOTHING';
      }
      return [
        {
          dateTime: startLocalDateTime,
          kind: 'START',
          item,
          type,
        },
        {
          dateTime: endLocalDateTime,
          kind: 'END',
          item,
          type,
        },
      ] as Array<TimeItem<T>>;
    }),
    [(item) => item.dateTime.toString(), (item) => item.kind],
    ['asc', 'desc']
  );
}
